import { api } from './client';

/** Standalone OAuth app (NOT Mini App) for browser login outside VK. */
export const VK_OAUTH_APP_ID = Number(import.meta.env.VITE_VK_OAUTH_APP_ID || 54827055);

const OAUTH_STATE = 'colibri_vkid';
const VERIFIER_KEY = 'colibri_pkce_verifier';

/** One-shot: a given ?code= must be exchanged exactly once (StrictMode / remounts). */
let callbackConsumed = false;

function saveVerifier(verifier: string) {
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  // Mobile browsers may drop sessionStorage across the id.vk.com redirect.
  localStorage.setItem(VERIFIER_KEY, verifier);
}

function loadVerifier(): string | undefined {
  return sessionStorage.getItem(VERIFIER_KEY) || localStorage.getItem(VERIFIER_KEY) || undefined;
}

function clearVerifier() {
  sessionStorage.removeItem(VERIFIER_KEY);
  localStorage.removeItem(VERIFIER_KEY);
}

/** Clear PKCE verifier + OAuth flow state from both storages (logout / error / cancel). */
export function clearOAuthFlow() {
  clearVerifier();
  localStorage.removeItem(OAUTH_STATE);
  sessionStorage.removeItem(OAUTH_STATE);
  localStorage.removeItem('vk_id_flow_pending');
  sessionStorage.removeItem('vk_id_flow_pending');
}

function b64url(bytes: Uint8Array): string {
  let s = '';
  bytes.forEach((b) => { s += String.fromCharCode(b); });
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomVerifier(): string {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return b64url(arr);
}

async function challengeS256(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return b64url(new Uint8Array(digest));
}

function redirectUri(): string {
  // VK ID пускает только redirect_uri из белого списка приложения — корень сайта.
  // Код приглашения переносится через localStorage, а не через URL.
  return `${window.location.origin}/`;
}

export async function buildVKIdAuthorizeUrl(): Promise<string> {
  const verifier = randomVerifier();
  saveVerifier(verifier);
  let challenge: string;
  try {
    challenge = await challengeS256(verifier);
  } catch (e) {
    clearOAuthFlow();
    throw e;
  }
  const params = new URLSearchParams({
    client_id: String(VK_OAUTH_APP_ID),
    display: 'mobile',
    redirect_uri: redirectUri(),
    response_type: 'code',
    scope: 'openid',
    state: OAUTH_STATE,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    v: '5.131',
  });
  return `https://id.vk.com/authorize?${params}`;
}

export interface VKIdCallback {
  code: string;
  device_id: string;
  state?: string;
}

/** Parse ?code=&device_id= after VK ID redirect. */
export function parseVKIdCallback(): VKIdCallback | null {
  const sp = new URLSearchParams(window.location.search);
  const code = sp.get('code');
  const device_id = sp.get('device_id');
  if (code && device_id) {
    return { code, device_id, state: sp.get('state') || undefined };
  }
  // also check hash query (HashRouter)
  const hash = window.location.hash;
  const q = hash.indexOf('?');
  if (q >= 0) {
    const hsp = new URLSearchParams(hash.slice(q + 1));
    const hc = hsp.get('code');
    const hd = hsp.get('device_id');
    if (hc && hd) {
      return { code: hc, device_id: hd, state: hsp.get('state') || undefined };
    }
  }
  return null;
}

/** Legacy implicit token (if VK ever returns hash tokens). */
export function parseOAuthHashToken(): { access_token: string; user_id: string } | null {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  if (!hash.includes('access_token=')) return null;
  const out: Record<string, string> = {};
  for (const pair of hash.split('&')) {
    const eq = pair.indexOf('=');
    if (eq < 0) continue;
    out[decodeURIComponent(pair.slice(0, eq))] = decodeURIComponent(pair.slice(eq + 1));
  }
  if (out.access_token && out.user_id) {
    return { access_token: out.access_token, user_id: out.user_id };
  }
  return null;
}

export function clearOAuthFromUrl(): void {
  const url = new URL(window.location.href);
  const sp = url.searchParams;
  for (const k of ['code', 'device_id', 'state', 'access_token', 'expires_in', 'user_id', 'token_type']) {
    sp.delete(k);
  }
  url.search = sp.toString();
  // Preserve hash route (HashRouter) — only strip oauth params if they leaked into the hash query
  const hash = url.hash;
  const q = hash.indexOf('?');
  if (q >= 0) {
    const hsp = new URLSearchParams(hash.slice(q + 1));
    for (const k of ['code', 'device_id', 'state', 'access_token', 'expires_in', 'user_id', 'token_type']) {
      hsp.delete(k);
    }
    const hs = hsp.toString();
    url.hash = hs ? hash.slice(0, q) + '?' + hs : hash.slice(0, q);
  }
  window.history.replaceState({}, '', url.toString());
}

/** Parse OAuth callback at most once per page load. */
export function takeVKIdCallback(): VKIdCallback | null {
  if (callbackConsumed) return null;
  const cb = parseVKIdCallback();
  if (cb) callbackConsumed = true;
  return cb;
}

/** Parse legacy hash-token callback at most once per page load. */
export function takeOAuthHashToken(): { access_token: string; user_id: string } | null {
  if (callbackConsumed) return null;
  const t = parseOAuthHashToken();
  if (t) callbackConsumed = true;
  return t;
}

export async function vkIdLogin(code: string, device_id: string) {
  const verifier = loadVerifier();
  const res = await api.post<{ token: string; user: unknown }>('auth/vk-id-login', {
    code,
    device_id,
    code_verifier: verifier,
    redirect_uri: redirectUri(),
    client_id: VK_OAUTH_APP_ID,
  }).catch((e) => {
    clearOAuthFlow();
    throw e;
  });
  localStorage.setItem('colibri_token', res.token);
  clearVerifier();
  return res;
}

export async function oauthLogin(access_token: string, user_id: number) {
  const res = await api.post<{ token: string; user: unknown }>('auth/oauth-login', {
    access_token,
    user_id,
  }).catch((e) => {
    clearOAuthFlow();
    throw e;
  });
  localStorage.setItem('colibri_token', res.token);
  return res;
}
